/**
 * QuickDelivery Pro — single source of truth for brand, plans and limits.
 *
 * This file is the ONLY place where prices, limits and public endpoints are
 * defined. It is synced verbatim into mobile/src/core and api/_lib/core by
 * `npm run sync:core`, so never edit those copies by hand.
 */

export const BRAND = {
  name: 'QuickDelivery Pro',
  shortName: 'QD Pro',
  vendor: 'Nexure LLC',
  vendorUrl: 'https://www.nexure.org',
  domain: 'quickdelivery.pro',
  siteUrl: 'https://quickdelivery.pro',
  supportEmail: 'support@quickdelivery.pro',
  androidPackage: 'pro.quickdelivery.app',
  // Marketing / UI palette. Moroccan flag red + green, kept from v1 so existing
  // users recognise the product.
  colors: {
    primary: '#C1272D',
    primaryDark: '#9B1F24',
    secondary: '#006233',
    ink: '#0B1220',
    muted: '#64748B',
    surface: '#FFFFFF',
    canvas: '#F7F8FA',
  },
} as const;

export type PlanId = 'free' | 'monthly' | 'annual' | 'lifetime';

/** Feature ceilings enforced by the limitation module. `null` means unlimited. */
export interface PlanLimits {
  maxClients: number | null;
  maxProducts: number | null;
  /** Orders that may be recorded per calendar month. */
  maxOrdersPerMonth: number | null;
  analytics: boolean;
  /** CSV / PDF export of orders and analytics. */
  export: boolean;
  /** Encrypted local backup file + restore. */
  backup: boolean;
  /** Number of devices one licence may be activated on. */
  maxDevices: number;
}

export const FREE_LIMITS: PlanLimits = {
  maxClients: 15,
  maxProducts: 10,
  maxOrdersPerMonth: 30,
  analytics: false,
  export: false,
  backup: false,
  maxDevices: 1,
};

export const PRO_LIMITS: PlanLimits = {
  maxClients: null,
  maxProducts: null,
  maxOrdersPerMonth: null,
  analytics: true,
  export: true,
  backup: true,
  maxDevices: 3,
};

/** Full-feature evaluation period granted on first launch. */
export const TRIAL_DAYS = 14;

/**
 * How long a Pro licence keeps working with no network. The activation token
 * carries `exp = period_end + OFFLINE_GRACE_DAYS`, so a driver in a dead zone
 * is never locked out mid-round.
 */
export const OFFLINE_GRACE_DAYS = 21;

/** Refresh the activation token once it is within this window of expiring. */
export const TOKEN_REFRESH_DAYS = 7;

export interface Plan {
  id: PlanId;
  /** Stripe Price ID. Filled in by scripts/stripe-setup.mjs. */
  priceId: string | null;
  /** Minor units (cents). */
  amount: number;
  currency: 'usd';
  interval: 'month' | 'year' | null;
  limits: PlanLimits;
  featured: boolean;
}

export const PLANS: Record<Exclude<PlanId, 'free'>, Plan> = {
  monthly: {
    id: 'monthly',
    priceId: 'price_1U9yJc2NrffEeecAFizY9LAY',
    amount: 999,
    currency: 'usd',
    interval: 'month',
    limits: PRO_LIMITS,
    featured: false,
  },
  annual: {
    id: 'annual',
    priceId: 'price_1U9yJi2NrffEeecABoOvNvIE',
    amount: 7900,
    currency: 'usd',
    interval: 'year',
    limits: PRO_LIMITS,
    featured: true,
  },
  lifetime: {
    id: 'lifetime',
    priceId: 'price_1U9yJo2NrffEeecAdQ3Lotqy',
    amount: 19900,
    currency: 'usd',
    interval: null,
    limits: PRO_LIMITS,
    featured: false,
  },
};

export function limitsForPlan(plan: PlanId): PlanLimits {
  return plan === 'free' ? FREE_LIMITS : PRO_LIMITS;
}

/**
 * Currencies offered by the price switcher on the marketing site.
 *
 * These are *display* conversions. Stripe charges in USD, because the prices
 * carry no `currency_options`, so every figure outside USD is shown with a "≈"
 * and the checkout currency is stated next to the switcher. Showing a exact
 * local figure that is not what the card is billed would be a lie, and the
 * kind that generates chargebacks.
 *
 * Rates are indicative and hand-maintained — they only have to be close enough
 * to answer "roughly what does this cost me?". To bill in local currency for
 * real, add `currency_options` to each Stripe Price and pass `currency` to the
 * Checkout session; then these become exact and the "≈" can go.
 */
export interface Currency {
  code: string;
  symbol: string;
  /** Units of this currency per 1 USD. */
  rate: number;
  locale: string;
  /** Symbol before the amount, as in $79, or after, as in 790 MAD. */
  symbolFirst: boolean;
  /** Most currencies read better rounded to whole units at these amounts. */
  decimals: 0 | 2;
}

/** Last reviewed: 2026-08-30. */
export const CURRENCIES: Currency[] = [
  { code: 'USD', symbol: '$',   rate: 1,     locale: 'en-US', symbolFirst: true,  decimals: 2 },
  { code: 'EUR', symbol: '€',   rate: 0.92,  locale: 'fr-FR', symbolFirst: false, decimals: 2 },
  { code: 'GBP', symbol: '£',   rate: 0.79,  locale: 'en-GB', symbolFirst: true,  decimals: 2 },
  { code: 'MAD', symbol: 'DH',  rate: 9.9,   locale: 'fr-MA', symbolFirst: false, decimals: 0 },
  { code: 'AED', symbol: 'AED', rate: 3.67,  locale: 'en-AE', symbolFirst: false, decimals: 0 },
  { code: 'SAR', symbol: 'SAR', rate: 3.75,  locale: 'en-SA', symbolFirst: false, decimals: 0 },
  { code: 'EGP', symbol: 'EGP', rate: 48.5,  locale: 'en-EG', symbolFirst: false, decimals: 0 },
  { code: 'CAD', symbol: 'CA$', rate: 1.37,  locale: 'en-CA', symbolFirst: true,  decimals: 2 },
  { code: 'AUD', symbol: 'A$',  rate: 1.52,  locale: 'en-AU', symbolFirst: true,  decimals: 2 },
  { code: 'CHF', symbol: 'CHF', rate: 0.88,  locale: 'de-CH', symbolFirst: false, decimals: 2 },
  { code: 'JPY', symbol: '¥',   rate: 152,   locale: 'ja-JP', symbolFirst: true,  decimals: 0 },
  { code: 'INR', symbol: '₹',   rate: 84,    locale: 'en-IN', symbolFirst: true,  decimals: 0 },
  { code: 'TRY', symbol: 'TL',  rate: 34.5,  locale: 'tr-TR', symbolFirst: false, decimals: 0 },
  { code: 'NGN', symbol: '₦',   rate: 1600,  locale: 'en-NG', symbolFirst: true,  decimals: 0 },
  { code: 'ZAR', symbol: 'R',   rate: 18.2,  locale: 'en-ZA', symbolFirst: true,  decimals: 0 },
];

/** The currency Stripe actually bills in. */
export const BILLING_CURRENCY = 'USD';

export function findCurrency(code: string): Currency {
  return CURRENCIES.find((c) => c.code === code) ?? CURRENCIES[0];
}

/**
 * Convert a USD minor-unit amount into `currency` and format it.
 * Zero-decimal currencies are rounded to a whole unit; the rest keep cents.
 */
export function convertPrice(usdMinor: number, currency: Currency): string {
  const value = (usdMinor / 100) * currency.rate;
  const rounded = currency.decimals === 0 ? Math.round(value) : Math.round(value * 100) / 100;
  const formatted = new Intl.NumberFormat(currency.locale, {
    minimumFractionDigits: currency.decimals === 0 ? 0 : (rounded % 1 === 0 ? 0 : 2),
    maximumFractionDigits: currency.decimals,
  }).format(rounded);
  return currency.symbolFirst ? `${currency.symbol}${formatted}` : `${formatted} ${currency.symbol}`;
}

export function formatPrice(amount: number, currency = 'USD', locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
  }).format(amount / 100);
}

/**
 * Ed25519 public key (base64url, 32 bytes) that signs activation tokens.
 * The matching private key lives only in the LICENSE_SIGNING_KEY env var on the
 * licence server — it is never shipped in a client bundle.
 */
export const LICENSE_PUBLIC_KEY = 'HyfDMiaHlps7fLtVECxOHZR7mGJhmq8CKsm9BXnc6EI';

export const API_BASE = `${BRAND.siteUrl}/api`;
